import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Form, Row, Input, Button, Spin, DatePicker } from 'antd';
import { SearchOutlined, CloseOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useFinalCustomerForm } from './hooks/useFinalCustomerForm';
import useFinalCustomerExtendedForm from './hooks/useFinalCustomerExtendedForm';
import { useDictionaryTranslation } from '../../hooks/useDictionaryTranslation';
import { useNotifications } from '../../hooks/useNotifications';
import { FormWrapper } from '../formWrapper/form-wrapper';
import { SelectCountry } from '../interoperableData/SelectCountry';
import { SelectCity } from '../interoperableData/SelectCity';
import { SelectDepartment } from '../interoperableData/SelectDepartment';
import { SelectDocumentType } from '../interoperableData/SelectDocumentType';
import { SelectBiologicalSex } from '../interoperableData/SelectBiologicalSex';
import { SelectBloodType } from '../interoperableData/SelectBloodType';
import { SelectHealthInsurance } from '../interoperableData/SelectHealthInsurance';
import { SelectRegime } from '../interoperableData/SelectRegime';
import SelectColor from '../selectColor/SelectColor';
import { useColorList } from '../../hooks/useColorList';
import { SelectTags } from '../selectTags/SelectTags';
import CountrySelectWithNumbersCustom from '../CountrySelectWithNumbersCustom/CountrySelectWithNumbersCustom';
import { IdentificationValidatorErrorModal } from '../IdentificationValidator/IdentificationValidatorErrorModal';
import { SelectScholarlyLevel } from '../interoperableData/SelectScholarlyLevel';
import { finalCustomerSolutionIdRules } from '../../config/constants/finalCustomerSolutionRuleConstants';
import { ExtendedFormProvider } from '../mobileForm/context/mobileFormContext';
import MobileForm from '../mobileForm/MobileForm';
import { mobileFormTypes } from '../mobileForm/hooks/useExtendedFormActions';
import { SelectPersonCondition } from '../interoperableData/SelectPersonCondition';
import { ErrorsModal } from '../mobileForm/componentsMobileForm/ErrorsModal';
import useFinalCustomerSettings from '../../views/AccountSetting/hooks/useFinalCustomersettings';
import SelectGenderIdentity from '../interoperableData/SelectGenderIdentity';
import SelectSexualOrientation from '../interoperableData/SelectSexualOrientation';
import { IdentificationFormItem } from '../forms/IdentificationFormItem';
import { useCanEditFinalCustomer } from '../../hooks/useCanEditFinalCustomer';

export const FormEditFinalCustomers = ({
  finalCustomerId,
  onFinish,
  formName = 'editFinalCustomersForm',
  displaySubmitButton = false,
  fullHeight = false,
  searchEnabled = false,
  onHandledSearch,
  disableFields,
}) => {
  const [form] = Form.useForm();
  const { getColorById } = useColorList();
  const { translate: t } = useDictionaryTranslation();
  const { showErrorNotification } = useNotifications();
  const [fieldMobile, setFieldMobile] = useState({ code: '', mobile: '' });
  const [showErrorsModal, setShowErrorsModal] = useState(false);
  const [selectedFinalCustomerId, setSelectedFinalcustomerId] = useState({ finalCustomerId, loadOnStart: true });
  const { solutionId } = useSelector((state) => ({
    solutionId: state.auth.accountData.solutionId,
  }));
  const canEditFinalCustomer = useCanEditFinalCustomer();

  const {
    finalCustomerData,
    createFinalCustomer,
    updateFinalCustomer,
    isLoading,
    errorsValidateDocument,
    setShowModalValidator,
    showModalValidator,
    getFinalCustomerByIdentification,
    resetFinalCustomerData,
  } = useFinalCustomerForm({
    finalCustomerId: selectedFinalCustomerId.finalCustomerId,
    loadOnStart: selectedFinalCustomerId.loadOnStart,
  });
  const [showCompletedForm, setShowCompletedForm] = useState(!searchEnabled || finalCustomerId);
  const selectedPersonCondition = Form.useWatch('personCondition', form);
  const selectedResidenceDepartment = Form.useWatch('residenceDepartment', form);
  const selectedDocumentType = Form.useWatch('documentType', form);
  const { dataSettings } = useFinalCustomerSettings();

  const selectedSolutionIdRules = finalCustomerSolutionIdRules[solutionId] ?? finalCustomerSolutionIdRules.default;

  // Editing an existing final customer is blocked for profiles without the
  // `canEditFinalCustomers` flag. Creating a new one (no loaded record) stays
  // available. `disableFields` from the parent still forces read-only mode.
  const isEditingExistingCustomer = Boolean(finalCustomerData?._id);
  const fieldsDisabled = disableFields || (isEditingExistingCustomer && !canEditFinalCustomer);

  const extendedFormValues = useMemo(() => finalCustomerData?.extendedForm || [], [finalCustomerData]);
  const idApp = useMemo(
    () => (dataSettings?.enableExtendedForm && dataSettings.extendedForm?._id ? dataSettings.extendedForm._id : ''),
    [dataSettings],
  );

  const formMobile = useFinalCustomerExtendedForm({
    idApp,
    values: extendedFormValues,
  });

  const fieldsList = formMobile?.getFieldList();

  const formWrapperStyles = useMemo(() => {
    const styles = {};
    if (fullHeight) styles.maxHeight = '100%';
    return styles;
  }, [fullHeight]);

  const setFormData = useCallback(async () => {
    if (!selectedFinalCustomerId.finalCustomerId || !finalCustomerData) return;
    setFieldMobile({
      code: finalCustomerData.mobilePhoneIndicative || '57',
      mobile: finalCustomerData.mobilePhone,
    });

    const birthdate = finalCustomerData.birthDate
      ? moment(finalCustomerData.birthDate, ['YYYY-MM-DD', 'DD/MM/YYYY']).format('YYYY-MM-DD')
      : null;

    form.setFieldsValue({
      nationalityCountry: finalCustomerData.nationalityCountryData,
      documentType: finalCustomerData.identificationTypeData,
      identification: finalCustomerData.identification,
      name: finalCustomerData.name,
      addressExtra: finalCustomerData.addressExtra,
      name1: finalCustomerData.firstName,
      name2: finalCustomerData.secondName,
      lastname1: finalCustomerData.lastName,
      lastname2: finalCustomerData.secondLastName,
      birthdate,
      biologicalSex: finalCustomerData.biologicalSexData,
      genderIdentity: finalCustomerData.genderIdentityData ?? {},
      sexualOrientation: finalCustomerData.sexualOrientationData ?? {},
      bloodType: finalCustomerData.bloodTypeData,
      residenceDepartment: finalCustomerData.residenceStateData,
      residenceCity: finalCustomerData.residenceCityData,
      address: finalCustomerData.address,
      healthInsurance: finalCustomerData.healthInsuranceData,
      regime: finalCustomerData.regimeData,
      email: finalCustomerData.email,
      color: finalCustomerData.idColor,
      tags: finalCustomerData.tags?.map((item) => ({ label: item.name, value: item._id })),
      landlinePhone: finalCustomerData.phone,
      mobilePhone: finalCustomerData.mobilePhone,
      mobilePhoneIndicative: finalCustomerData.mobilePhoneIndicative,
      optionalMobilePhone: finalCustomerData.optionalMobilePhone,
      scholarlyLevel: finalCustomerData.scholarlyLevelData,
      personCondition: finalCustomerData.conditionData,
    });
  }, [finalCustomerData, form, showErrorNotification, getColorById]);

  const getFormValues = () => {
    const values = form.getFieldsValue();

    const birthDateValue = values.birthdate ? moment(values.birthdate).format('YYYY-MM-DD') : null;
    return {
      nationalityCountry: values.nationalityCountry?._id,
      identificationType: values.documentType.value,
      identification: values.identification,
      name: values.name,
      addressExtra: values.addressExtra,
      firstName: values.name1,
      secondName: values.name2,
      lastName: values.lastname1,
      secondLastName: values.lastname2,
      birthDate: birthDateValue,
      biologicalSex: values.biologicalSex?.value,
      genderIdentity: values.genderIdentity?.value ?? null,
      sexualOrientation: values.sexualOrientation?.value ?? null,
      bloodType: values.bloodType?.description,
      residenceState: values.residenceDepartment?._id,
      residenceCity: values.residenceCity?._id,
      address: values.address,
      healthInsuranceCode: values.healthInsurance?._id,
      regime: values.regime?._id,
      email: values.email,
      idColor: values.color,
      tags: values.tags?.map((item) => item.value),
      phone: values.landlinePhone,
      mobilePhone: fieldMobile.mobile,
      mobilePhoneIndicative: fieldMobile.code,
      optionalMobilePhone: values.optionalMobilePhone,
      scholarlyLevel: values.scholarlyLevel?.value,
      condition: selectedPersonCondition?.value,
    };
  };

  const saveUpdates = async ({ itemId, newValues, showMessage }) => {
    if (idApp) {
      const extendedFormData = await formMobile.getStructure(true, itemId);
      newValues ??= {};
      newValues.extendedForm = extendedFormData.resultStructure;
    }
    if (!newValues) return;
    return updateFinalCustomer(newValues, itemId, showMessage);
  };

  const submitFormData = async () => {
    // Guard against editing an existing final customer without permission.
    if (isEditingExistingCustomer && !canEditFinalCustomer) return;
    try {
      const formValues = getFormValues();
      const resultValidations = formMobile.values.updateFormValues(true);
      if (resultValidations.hasError) {
        setShowErrorsModal(true);
        return;
      }

      const newValues = {
        ...formValues,
      };

      let customerData;
      if (selectedFinalCustomerId.finalCustomerId) {
        customerData = await saveUpdates({
          itemId: selectedFinalCustomerId.finalCustomerId,
          newValues,
          showMessage: true,
        });
      } else {
        customerData = await createFinalCustomer(newValues);
        const newCustomerData = await saveUpdates({
          itemId: customerData._id,
          showMessage: false,
        });
        customerData = newCustomerData ?? customerData;
      }

      onFinish(customerData);
      form.resetFields();
    } catch (error) {
      console.error(error);
    }
  };

  const resetSearch = () => {
    resetFinalCustomerData();
    form.resetFields();
    setSelectedFinalcustomerId({ finalCustomerId: null, loadOnStart: false });
    setShowCompletedForm(false);
    onHandledSearch({ searchReset: true });
  };

  const onSearch = async () => {
    setShowCompletedForm(true);

    const identification = form.getFieldValue('identification');

    onHandledSearch({ searchDone: true });

    if (!identification || !selectedDocumentType) return;

    form.resetFields();
    const newFinalCustomerId = await getFinalCustomerByIdentification(selectedDocumentType.value, identification);

    setSelectedFinalcustomerId({ finalCustomerId: newFinalCustomerId, loadOnStart: false });

    if (!newFinalCustomerId) {
      form.setFieldsValue({ documentType: selectedDocumentType, identification });
    }
  };

  const closeModal = () => {
    setShowModalValidator(false);
  };

  const closeErrorsModal = () => {
    setShowErrorsModal(false);
  };

  useEffect(() => {
    const timeout = setTimeout(setFormData, 100);
    return () => clearTimeout(timeout);
  }, [setFormData]);

  useEffect(() => form.resetFields, [form]);

  return (
    <FormWrapper style={formWrapperStyles}>
      <Spin spinning={isLoading}>
        <Form form={form} onFinish={submitFormData} layout="vertical" name={formName}>
          <Row>
            <Col xs={24} md={12}>
              <Form.Item
                name="documentType"
                rules={[{ required: true, message: t('documentTypeRequired') }]}
                label={t('documentType')}
                valuePropName="selectedDocumentType"
              >
                <SelectDocumentType disabled={fieldsDisabled} placeholder={t('documentTypePlaceholder')} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <IdentificationFormItem
                isAutogenerated={!selectedDocumentType?.rules?.isAutogenerated}
                disabled
              >
                {!searchEnabled ? (
                  <Input
                    placeholder={t('identificationPlaceholder')}
                    disabled={fieldsDisabled || selectedDocumentType?.rules?.isAutogenerated}
                    autoComplete="off"
                  />
                ) : (
                  <Input.Search
                    placeholder={t('identificationPlaceholder')}
                    enterButton={<SearchOutlined />}
                    size="large"
                    onSearch={onSearch}
                    loading={isLoading}
                    autoComplete="off"
                    suffix={
                      showCompletedForm && (
                        <Button
                          type="text"
                          shape="circle"
                          size="small"
                          onClick={resetSearch}
                          danger
                          icon={<CloseOutlined />}
                        />
                      )
                    }
                    disabled={disableFields || (showCompletedForm && selectedDocumentType?.rules?.isAutogenerated)}
                  />
                )}

              </IdentificationFormItem>
            </Col>
            {showCompletedForm && selectedDocumentType?.rules?.requiresCondition ? (
              <Col xs={24} md={12}>
                <Form.Item
                  name="personCondition"
                  rules={[{ required: true, message: t('fieldIsRequired') }]}
                  label={t('personCondition')}
                  valuePropName="selectedPersonCondition"
                >
                  <SelectPersonCondition disabled={fieldsDisabled} placeholder={t('personConditionPlaceholder')} />
                </Form.Item>
              </Col>
            ) : null}

            {showCompletedForm ? (
              <>
                {selectedSolutionIdRules.name ? (
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="name"
                      rules={[
                        { required: true, message: t('name1Required') },
                        {
                          max: 50,
                          message: t('lengthMaxValidation', { label: t('name1Required'), max: 50 }),
                        },
                      ]}
                      label={t('name')}
                    >
                      <Input placeholder={t('name1Placeholder')} disabled={fieldsDisabled} />
                    </Form.Item>
                  </Col>
                ) : null}

                {selectedSolutionIdRules.showName1 ? (
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="name1"
                      rules={[
                        { required: true, message: t('name1Required') },
                        {
                          max: 50,
                          message: t('lengthMaxValidation', { label: t('name1Required'), max: 50 }),
                        },
                      ]}
                      label={t('name1')}
                    >
                      <Input placeholder={t('name1Placeholder')} disabled={fieldsDisabled} />
                    </Form.Item>
                  </Col>
                ) : null}
                {selectedSolutionIdRules.showName2 ? (
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="name2"
                      rules={[
                        {
                          max: 50,
                          message: t('lengthMaxValidation', { label: t('name2Required'), max: 50 }),
                        },
                      ]}
                      label={t('name2')}
                    >
                      <Input placeholder={t('name2Placeholder')} disabled={fieldsDisabled} />
                    </Form.Item>
                  </Col>
                ) : null}
                {selectedSolutionIdRules.showlastname1 ? (
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="lastname1"
                      rules={[
                        { required: true, message: t('lastname1Required') },
                        {
                          max: 50,
                          message: t('lengthMaxValidation', { label: t('lastname1Required'), max: 50 }),
                        },
                      ]}
                      label={t('lastname1')}
                    >
                      <Input placeholder={t('lastname1Placeholder')} disabled={fieldsDisabled} />
                    </Form.Item>
                  </Col>
                ) : null}

                {selectedSolutionIdRules.showlastname2 ? (
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="lastname2"
                      rules={[
                        {
                          max: 50,
                          message: t('lengthMaxValidation', { label: t('lastname2Required'), max: 50 }),
                        },
                      ]}
                      label={t('lastname2')}
                    >
                      <Input placeholder={t('lastname2Placeholder')} disabled={fieldsDisabled} />
                    </Form.Item>
                  </Col>
                ) : null}

                {selectedSolutionIdRules.birthdate ? (
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="birthdate"
                      rules={[
                        { required: dataSettings?.fieldsInfo?.birthDate?.required, message: t('birthdateRequired') },
                      ]}
                      label={t('birthDate')}
                      getValueProps={(value) => ({
                        value: value ? moment(value) : null,
                      })}
                      normalize={(value) => (value ? moment(value).format('YYYY-MM-DD') : '')}
                    >
                      <DatePicker
                        placeholder={t('enterDatePlaceholder')}
                        format="DD/MM/YYYY"
                        disabled={fieldsDisabled}
                        disabledDate={(current) => current && current > moment().endOf('day')}
                      />
                    </Form.Item>
                  </Col>
                ) : null}

                {selectedSolutionIdRules.biologicalSex ? (
                  <>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="biologicalSex"
                        rules={[{ required: true, message: t('biologicalSexRequired') }]}
                        label={t('biologicalSex')}
                        valuePropName="selectedBiologicalSex"
                      >
                        <SelectBiologicalSex placeholder={t('biologicalSexPlaceholder')} disabled={fieldsDisabled} />
                      </Form.Item>
                    </Col>

                    {selectedSolutionIdRules.genderIdentity ? (
                      <Col xs={24} md={12}>
                        <Form.Item name="genderIdentity" label={t('genderIdentity')}>
                          <SelectGenderIdentity disabled={fieldsDisabled} />
                        </Form.Item>
                      </Col>
                    ) : null}

                    {selectedSolutionIdRules.sexualOrientation ? (
                      <Col xs={24} md={12}>
                        <Form.Item name="sexualOrientation" label={t('sexualOrientation')}>
                          <SelectSexualOrientation
                            placeholder={t('biologicalSexPlaceholder')}
                            disabled={fieldsDisabled}
                          />
                        </Form.Item>
                      </Col>
                    ) : null}
                  </>
                ) : null}

                {selectedSolutionIdRules.showMobilePhone ? (
                  <>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="mobilePhone"
                        rules={[{ required: true, message: t('mobilePhoneRequired') }]}
                        label={t('mobilePhone')}
                      >
                        <CountrySelectWithNumbersCustom
                          onChange={setFieldMobile}
                          fieldMobile={fieldMobile}
                          disabled={fieldsDisabled}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item name="optionalMobilePhone" label={t('optionalMobilePhone')}>
                        <Input placeholder={t('optionalMobilePhonePlaceholder')} disabled={fieldsDisabled} />
                      </Form.Item>
                    </Col>
                  </>
                ) : null}

                <Col xs={24} md={12}>
                  <Form.Item
                    name="email"
                    rules={[
                      {
                        required: selectedSolutionIdRules.emailRequired,
                        message: t('emailRequired'),
                      },
                      { type: 'email', message: t('isNotAValidEmail', { label: t('email') }) },
                    ]}
                    label={t('email')}
                  >
                    <Input placeholder={t('emailPlaceholder')} type="email" disabled={fieldsDisabled} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    name="landlinePhone"
                    rules={[
                      {
                        required: selectedSolutionIdRules.landlinePhoneRequired,
                        message: t('landlinePhoneRequired'),
                      },
                    ]}
                    label={t('landlinePhone')}
                  >
                    <Input placeholder={t('landlinePhoneNumberPlaceholder')} disabled={fieldsDisabled} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    name="nationalityCountry"
                    rules={[
                      {
                        required: selectedPersonCondition?.rules?.requiresCountry,
                        message: t('nationalityCountryRequired'),
                      },
                    ]}
                    label={t('nationalityCountry')}
                    valuePropName="selectedCountry"
                  >
                    <SelectCountry placeholder={t('nationalityCountryPlaceholder')} disabled={fieldsDisabled} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    name="address"
                    rules={[
                      {
                        required: dataSettings?.fieldsInfo?.address?.required,
                      },
                    ]}
                    label={t('address')}
                  >
                    <Input placeholder={t('addressPlaceholder')} disabled={fieldsDisabled} />
                  </Form.Item>
                </Col>

                {selectedSolutionIdRules.addressExtra ? (
                  <Col xs={24} md={12}>
                    <Form.Item name="addressExtra" label={t('addressComplement')}>
                      <Input placeholder={t('addressPlaceholder')} disabled={fieldsDisabled} />
                    </Form.Item>
                  </Col>
                ) : null}

                <Col xs={24} md={12}>
                  <Form.Item
                    name="residenceDepartment"
                    rules={[
                      {
                        required: selectedDocumentType?.rules?.requiresDepartment,
                        message: t('residenceDepartmentRequired'),
                      },
                    ]}
                    label={t('residenceDepartment')}
                    valuePropName="selectedDepartment"
                  >
                    <SelectDepartment placeholder={t('residenceDepartmentPlaceholder')} disabled={fieldsDisabled} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    name="residenceCity"
                    rules={[
                      { required: selectedDocumentType?.rules?.requiresCity, message: t('residenceCityRequired') },
                    ]}
                    label={t('residenceCity')}
                    valuePropName="selectedCity"
                  >
                    <SelectCity
                      stateCode={selectedResidenceDepartment?.iso_state_code}
                      placeholder={t('residenceCityPlaceholder')}
                      disabled={fieldsDisabled}
                    />
                  </Form.Item>
                </Col>

                {selectedSolutionIdRules.showBloodType ? (
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="bloodType"
                      rules={[{ required: false, message: t('bloodTypeRequired') }]}
                      label={t('bloodType')}
                      valuePropName="selectedBloodType"
                    >
                      <SelectBloodType placeholder={t('bloodTypePlaceholder')} disabled={fieldsDisabled} />
                    </Form.Item>
                  </Col>
                ) : null}

                {selectedSolutionIdRules.showhealthInsurance ? (
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="healthInsurance"
                      rules={[{ required: false, message: t('healthInsuranceRequired') }]}
                      label={t('healthInsurance')}
                      valuePropName="selectedHealthInsurance"
                    >
                      <SelectHealthInsurance placeholder={t('healthInsurancePlaceholder')} disabled={fieldsDisabled} />
                    </Form.Item>
                  </Col>
                ) : null}

                {selectedSolutionIdRules.showRegime ? (
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="regime"
                      label={t('regime')}
                      rules={[{ required: false, message: t('regimeRequired') }]}
                      valuePropName="selectedRegime"
                    >
                      <SelectRegime placeholder={t('regimePlaceholder')} disabled={fieldsDisabled} />
                    </Form.Item>
                  </Col>
                ) : null}

                {selectedSolutionIdRules.showScholarlyLevel ? (
                  <Col xs={24} md={12}>
                    <Form.Item name="scholarlyLevel" label={t('scholarlyLevel')} valuePropName="selectedScholarlyLevel">
                      <SelectScholarlyLevel placeholder={t('scholarlyLevelPlaceholder')} disabled={fieldsDisabled} />
                    </Form.Item>
                  </Col>
                ) : null}

                <Col xs={24} md={12}>
                  <Form.Item
                    name="color"
                    rules={[{ required: false, message: t('colorRequired') }]}
                    label={t('colorMark')}
                  >
                    <SelectColor disabled={fieldsDisabled} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item name="tags" rules={[{ required: false, message: t('tagsRequired') }]} label={t('tags')}>
                    <SelectTags multiple disabled={fieldsDisabled} />
                  </Form.Item>
                </Col>
                {dataSettings?.enableExtendedForm && idApp ? (
                  <Col md={24}>
                    <ExtendedFormProvider itemId={finalCustomerId} itemType={mobileFormTypes.finalCustomer}>
                      <MobileForm
                        fields={fieldsList}
                        formValues={formMobile.values.mobileFormValues}
                        onChange={formMobile.values.updateFieldValue}
                        disabled={fieldsDisabled}
                      />
                    </ExtendedFormProvider>
                  </Col>
                ) : null}
              </>
            ) : null}
          </Row>
          {displaySubmitButton ? (
            <Row justify="end">
              <Button type="primary" disabled={isLoading} htmlType="submit">
                {t('save')}
              </Button>
            </Row>
          ) : null}
        </Form>
      </Spin>
      <IdentificationValidatorErrorModal
        isVisible={showModalValidator}
        errors={errorsValidateDocument}
        onClose={closeModal}
        selectedDocumentType={selectedDocumentType}
      />
      {showErrorsModal && formMobile.errors?.formErrors?.length ? (
        <ErrorsModal show={showErrorsModal} errors={formMobile.errors?.formErrors} onClose={closeErrorsModal} />
      ) : null}
    </FormWrapper>
  );
};

FormEditFinalCustomers.propTypes = {
  finalCustomerId: PropTypes.string,
  displaySubmitButton: PropTypes.bool,
  onFinish: PropTypes.func,
  formName: PropTypes.string,
  fullHeight: PropTypes.bool,
  searchEnabled: PropTypes.bool,
  onHandledSearch: PropTypes.func,
  disableFields: PropTypes.bool,
};
